/**
 * WebSocket Manager for Real-Time Job Updates
 * Handles connections to backend WebSocket endpoints for live progress notifications
 */

type JobUpdateCallback = (data: JobUpdate) => void;

export interface JobUpdate {
  type: 'progress' | 'complete' | 'error' | 'status';
  job_id: string;
  status?: string;
  total?: number;
  processed?: number;
  analyzed?: number;
  percent?: number;
  results?: any[];
  error?: string;
  message?: string;
}

class WebSocketManager {
  private connections: Map<string, WebSocket> = new Map();
  private callbacks: Map<string, Set<JobUpdateCallback>> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;

  /**
   * Subscribe to updates for a specific job
   */
  subscribeToJob(jobId: string, callback: JobUpdateCallback): () => void {
    // Add callback to the set for this job
    if (!this.callbacks.has(jobId)) {
      this.callbacks.set(jobId, new Set());
    }
    this.callbacks.get(jobId)!.add(callback);

    // Create WebSocket connection if not exists
    if (!this.connections.has(jobId)) {
      this.createConnection(jobId);
    }

    // Return unsubscribe function
    return () => {
      const callbacks = this.callbacks.get(jobId);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.closeConnection(jobId);
        }
      }
    };
  }

  private createConnection(jobId: string): void {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsUrl = backendUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    
    const wsEndpoint = `${wsUrl}/api/v1/ws/jobs/${jobId}`;
    
    console.log(`🔌 WebSocket: Connecting to ${wsEndpoint}`);
    
    try {
      const ws = new WebSocket(wsEndpoint);
      
      ws.onopen = () => {
        console.log(`✅ WebSocket: Connected for job ${jobId}`);
        this.reconnectAttempts.set(jobId, 0);
      };
      
      ws.onmessage = (event) => {
        try {
          const data: JobUpdate = JSON.parse(event.data);
          console.log(`📨 WebSocket: Update for job ${jobId}:`, data.type, data.percent ? `${data.percent}%` : '');
          
          // Notify all callbacks for this job
          const callbacks = this.callbacks.get(jobId);
          if (callbacks) {
            callbacks.forEach(cb => cb(data));
          }
          
          // Auto-close on completion
          if (data.type === 'complete' || data.status === 'completed' || data.status === 'failed') {
            console.log(`🏁 WebSocket: Job ${jobId} finished, closing connection`);
            this.closeConnection(jobId);
          }
        } catch (err) {
          console.warn('WebSocket: Failed to parse message:', err);
        }
      };
      
      ws.onerror = (error) => {
        console.error(`❌ WebSocket: Error for job ${jobId}:`, error);
      };
      
      ws.onclose = (event) => {
        console.log(`🔌 WebSocket: Disconnected from job ${jobId} (code: ${event.code})`);
        this.connections.delete(jobId);
        
        // Attempt reconnect if we still have callbacks and haven't exceeded attempts
        const callbacks = this.callbacks.get(jobId);
        if (callbacks && callbacks.size > 0) {
          const attempts = this.reconnectAttempts.get(jobId) || 0;
          if (attempts < this.maxReconnectAttempts) {
            this.reconnectAttempts.set(jobId, attempts + 1);
            console.log(`🔄 WebSocket: Reconnecting to job ${jobId} (attempt ${attempts + 1}/${this.maxReconnectAttempts})`);
            setTimeout(() => this.createConnection(jobId), this.reconnectDelay);
          } else {
            console.warn(`⚠️ WebSocket: Max reconnect attempts reached for job ${jobId}`);
          }
        }
      };
      
      this.connections.set(jobId, ws);
      
      // Set up ping/pong to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        } else {
          clearInterval(pingInterval);
        }
      }, 30000);
      
    } catch (err) {
      console.error(`❌ WebSocket: Failed to create connection for job ${jobId}:`, err);
    }
  }

  private closeConnection(jobId: string): void {
    const ws = this.connections.get(jobId);
    if (ws) {
      ws.close();
      this.connections.delete(jobId);
    }
    this.callbacks.delete(jobId);
    this.reconnectAttempts.delete(jobId);
  }

  /**
   * Close all connections
   */
  closeAll(): void {
    this.connections.forEach((ws, jobId) => {
      ws.close();
    });
    this.connections.clear();
    this.callbacks.clear();
    this.reconnectAttempts.clear();
  }
}

// Singleton instance
export const wsManager = new WebSocketManager();
